import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { TipItem } from "@/components/TipItem";
import { supabase } from "@/integrations/supabase/client";
import { getHistory, updateAnalysisNotes } from "@/lib/history.functions";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";
import type { Locale } from "@/i18n/types";
import { NotesAndActionsField, type UserAction } from "@/components/NotesAndActionsField";
import { toast } from "sonner";

export const Route = createFileRoute("/history")({
  head: () => ({
    meta: [
      { title: "Historique des conseils — Glow Up Coach" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HistoryPage,
});

const GOAL_LABEL: Record<Locale, Record<string, string>> = {
  fr: { skin: "Peau & Éclat", weight: "Forme physique", muscle: "Tonus musculaire", antiaging: "Anti-âge & Longévité" },
  en: { skin: "Skin & Glow", weight: "Fitness", muscle: "Muscle tone", antiaging: "Anti-ageing & Longevity" },
  nl: { skin: "Huid & glans", weight: "Fitheid", muscle: "Spiertonus", antiaging: "Anti-veroudering & levensduur" },
};

function HistoryPage() {
  useDocumentTitleKey("history.head.title");
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const fetchHistory = useServerFn(getHistory);
  const updateNotes = useServerFn(updateAnalysisNotes);
  const qc = useQueryClient();
  const [authed, setAuthed] = useState(false);
  const [editing, setEditing] = useState<{ id: string; notes: string; actions: UserAction[] } | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else setAuthed(true);
    });
  }, [nav]);

  const { data, isLoading } = useQuery({
    queryKey: ["history"],
    queryFn: () => fetchHistory({ data: undefined as any }),
    enabled: authed,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string; user_notes: string | null; user_actions: UserAction[] | null }) =>
      updateNotes({ data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history"] });
      toast.success(t("notes.saved"));
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("history.kicker")}</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("history.title")}
        </h1>
        <p className="text-muted-foreground mb-10">{t("history.subtitle")}</p>

        <div className="mb-8 -mt-6 flex flex-wrap gap-3 text-sm">
          <Link
            to="/settings"
            hash="consents"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground/15 hover:bg-muted font-semibold"
          >
            ⚙ {locale === "fr" ? "Modifier mes consentements (photos)" : locale === "nl" ? "Toestemmingen aanpassen (foto's)" : "Manage photo consents"}
          </Link>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t("history.loading")}</p>
        ) : !data?.items?.length ? (
          <div className="p-8 rounded-3xl border border-foreground/10 bg-muted text-center">
            <p className="font-semibold mb-2">{t("history.empty.title")}</p>
            <p className="text-sm text-muted-foreground mb-6">{t("history.empty.desc")}</p>
            <Link to="/app" className="bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90">
              {t("history.empty.cta")}
            </Link>
          </div>
        ) : (
          <ul className="space-y-8">
            {data.items.map((item) => {
              const d = new Date(item.created_at);
              return (
                <li
                  key={item.id}
                  className="rounded-3xl border border-foreground/10 bg-background overflow-hidden"
                >
                  <div className="flex flex-col md:flex-row">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={t("history.photo_alt")}
                        className="md:w-48 w-full h-48 md:h-auto object-cover"
                      />
                    ) : (
                      <div className="md:w-48 w-full md:min-h-[12rem] bg-muted grid place-items-center p-4 text-center">
                        <p className="text-[11px] text-muted-foreground leading-snug">
                          {t("history.no_photo")}
                        </p>
                      </div>
                    )}
                    <div className="flex-1 p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          {d.toLocaleString(locale, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider bg-accent/10 text-accent px-2 py-0.5 rounded-full font-bold">
                          {GOAL_LABEL[locale][item.goal] ?? item.goal}
                        </span>
                        {item.score != null && (
                          <span className="text-xs font-bold ml-auto">{item.score}/100</span>
                        )}
                      </div>
                      {item.summary && (
                        <p className="text-sm text-foreground/80 mb-4">{item.summary}</p>
                      )}
                      {(item.user_actions?.length || item.user_notes) && (
                        <div className="mb-4 rounded-xl border border-foreground/10 bg-muted/40 p-3">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                            {t("notes.section_title")}
                          </p>
                          {item.user_actions && item.user_actions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {item.user_actions.map((a) => (
                                <span key={`${a.kind}-${a.label}`} className="text-[11px] bg-background border border-foreground/10 px-2 py-0.5 rounded-full">
                                  {a.label}
                                </span>
                              ))}
                            </div>
                          )}
                          {item.user_notes && (
                            <p className="text-xs text-foreground/80 whitespace-pre-wrap">{item.user_notes}</p>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            id: item.id,
                            notes: item.user_notes ?? "",
                            actions: (item.user_actions ?? []) as UserAction[],
                          })
                        }
                        className="text-xs font-semibold text-accent hover:underline mb-3"
                      >
                        {item.user_notes || item.user_actions?.length ? t("notes.edit") : t("notes.add")}
                      </button>
                      {item.tips && item.tips.length > 0 && (
                        <ul className="space-y-4 mt-4">
                          {item.tips.map((tip, i) => (
                            <TipItem
                              key={i}
                              index={i}
                              tip={tip}
                              numberClassName="text-accent font-display text-xl font-bold leading-none"
                              detailClassName="text-muted-foreground text-xs leading-relaxed"
                              badgeClassName="text-[10px] uppercase tracking-wider bg-foreground/5 text-foreground/70 px-2 py-0.5 rounded-full"
                              disclaimerClassName="text-[11px] text-accent mt-2 italic"
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-10 text-center">
          <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground">
            {t("history.back")}
          </Link>
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 bg-foreground/40 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-background w-full max-w-lg rounded-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-xl mb-4">{t("notes.modal.title")}</h2>
            <NotesAndActionsField
              notes={editing.notes}
              onNotesChange={(v) => setEditing((p) => (p ? { ...p, notes: v } : p))}
              actions={editing.actions}
              onActionsChange={(v) => setEditing((p) => (p ? { ...p, actions: v } : p))}
            />
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 py-3 rounded-xl border border-foreground/15 text-sm font-semibold"
              >
                {t("notes.cancel")}
              </button>
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    id: editing.id,
                    user_notes: editing.notes.trim() ? editing.notes.trim() : null,
                    user_actions: editing.actions.length > 0 ? editing.actions : null,
                  })
                }
                className="flex-1 py-3 rounded-xl bg-foreground text-background text-sm font-semibold disabled:opacity-50"
              >
                {saveMutation.isPending ? "…" : t("notes.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
